/**
 * @fileoverview Initializes the client-side i18next instance with backend loading and sensible defaults.
 * @module lib/i18n
 */

import i18next, { type InitOptions, type Resource } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import HttpBackend from 'i18next-http-backend'
import { initReactI18next } from 'react-i18next'

const isBrowser = typeof window !== 'undefined'
const isTestEnv = import.meta.env.MODE === 'test'

const fallbackResources: Resource = {
  en: {
    common: {
      userMenu: {
        label: 'User',
        logOut: 'Log out',
        language: 'Language',
        english: 'English',
        polish: 'Polish',
      },
    },
  },
  pl: {
    common: {
      userMenu: {
        label: 'Użytkownik',
        logOut: 'Wyloguj',
        language: 'Język',
        english: 'Angielski',
        polish: 'Polski',
      },
    },
  },
}

const sharedNamespaces = ['common', 'navigation'] as const

const initOptions: InitOptions = {
  fallbackLng: 'en',
  supportedLngs: ['en', 'pl'],
  defaultNS: 'common',
  fallbackNS: ['common'],
  ns: sharedNamespaces,
  interpolation: { escapeValue: false },
  // Prevent full-tree blank states when namespace loading stalls (e.g. tab resume/network hiccups).
  react: { useSuspense: false },
  returnNull: false,
}

if (isTestEnv) {
  initOptions.lng = 'en'
  initOptions.resources = fallbackResources
  initOptions.initImmediate = false
} else {
  initOptions.backend = {
    loadPath: '/locales/{{lng}}/{{ns}}.json',
  }
}

if (!isTestEnv) {
  void i18next.use(HttpBackend)
}

if (isBrowser && !isTestEnv) {
  void i18next.use(LanguageDetector)
}

void i18next.use(initReactI18next).init(initOptions)

export { i18next }
